import { useCallback, useEffect, useState } from 'react';

export type PermissionState = 'prompt' | 'granted' | 'denied' | 'unknown';

export interface GeolocationState {
    latitude: number | null;
    longitude: number | null;
    loading: boolean;
    error: string | null;
    /** Browser-level permission state for geolocation. */
    permissionState: PermissionState;
    /** Re-request geolocation. Only useful when permissionState is 'prompt' or 'granted'. */
    retry: () => void;
}

function requestPosition(
    onSuccess: (pos: GeolocationPosition) => void,
    onError: (err: GeolocationPositionError) => void,
) {
    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        enableHighAccuracy: false,
        timeout: 10000,
    });
}

export function useGeolocation(enabled = true): GeolocationState {
    const [state, setState] = useState<Omit<GeolocationState, 'retry'>>({
        latitude: null,
        longitude: null,
        loading: true,
        error: null,
        permissionState: 'unknown',
    });

    // Query the Permissions API and listen for changes
    useEffect(() => {
        if (!navigator.permissions) return;

        let status: PermissionStatus | null = null;

        const onChange = () => {
            if (status) {
                setState(s => ({ ...s, permissionState: status!.state as PermissionState }));
            }
        };

        navigator.permissions.query({ name: 'geolocation' }).then(result => {
            status = result;
            setState(s => ({ ...s, permissionState: result.state as PermissionState }));
            result.addEventListener('change', onChange);
        }).catch(() => {
            // Permissions API not supported — leave as 'unknown'
        });

        return () => {
            if (status) status.removeEventListener('change', onChange);
        };
    }, []);

    const doRequest = useCallback(() => {
        // Allow spoofing location via ?lat=...&lng=... for testing
        const params = new URLSearchParams(window.location.search);
        const spoofLat = params.get('lat');
        const spoofLng = params.get('lng');
        if (spoofLat && spoofLng) {
            const lat = parseFloat(spoofLat);
            const lng = parseFloat(spoofLng);
            if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
                setState(s => ({ ...s, latitude: lat, longitude: lng, loading: false, error: null }));
                return;
            }
        }

        if (!navigator.geolocation) {
            setState(s => ({ ...s, loading: false, error: 'Geolocation not supported' }));
            return;
        }

        setState(s => ({ ...s, loading: true, error: null }));

        requestPosition(
            (pos) => {
                setState(s => ({
                    ...s,
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    loading: false,
                    error: null,
                }));
            },
            (err) => {
                setState(s => ({ ...s, loading: false, error: err.message }));
            },
        );
    }, []);

    // Request once enabled (deferred when starting on a non-discovery view)
    useEffect(() => {
        if (enabled) doRequest();
    }, [enabled, doRequest]);

    return { ...state, retry: doRequest };
}
